/**
 * Copy Style / Apply … (Format Tools › Copy Style) — ported from the VSTO
 * plugin's `PickApplyFormatting.cs`.
 *
 * "Copy Style" snapshots the current selection into a module-level clipboard;
 * each "Apply …" tool then writes one facet of that snapshot onto the new
 * selection. As in VSTO the snapshot is indexed **cyclically**: pick 2 shapes,
 * select 6, and the picked pair repeats three times — that is what makes
 * "Apply Width" on an alternating row work.
 *
 * Deliberate improvements over VSTO, which implemented "Apply Shape Style" and
 * "Apply all formatting" by copy/pasting the *source* shape over the target and
 * then `Delete()`-ing the target:
 *  • Targets keep their identity (id, name, z-order, animations, hyperlinks) —
 *    only the requested formatting is written.
 *  • The source slide no longer has to still exist, so a style survives the
 *    source slide being edited or deleted.
 *  • Text is never overwritten by the source's text.
 * The captured facets are also wider than VSTO's: line dash/style/transparency,
 * fill transparency, bold/italic/underline, word wrap, vertical anchor,
 * autosize and text-frame margins all come along.
 */
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { assertNever } from "../../../utils/assertNever";
import type { ApplyStyleFacet, CopiedShapeStyle, CopiedStyle, TextMargins } from "../../../types";

/**
 * The style clipboard. Module-level (not React state) so it survives tab
 * switches, panel re-mounts and command-palette invocations — matching the
 * VSTO add-in, where the pick lived on the long-lived `PickApplyFormatting`.
 */
let clipboard: CopiedStyle | null = null;

/** Listeners notified whenever the clipboard is filled or cleared. */
const listeners = new Set<(style: CopiedStyle | null) => void>();

/** Subscribe to clipboard changes; returns the unsubscribe function. */
export function onCopiedStyleChange(fn: (style: CopiedStyle | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The current style clipboard, or null when nothing has been picked yet. */
export function getCopiedStyle(): CopiedStyle | null {
  return clipboard;
}

/** Drop the picked style (used by the panel's "Clear" affordance and by tests). */
export function clearCopiedStyle(): void {
  setClipboard(null);
}

function setClipboard(next: CopiedStyle | null): void {
  clipboard = next;
  for (const fn of listeners) fn(next);
}

/** Every preset shape exposes at most this many adjustment handles. */
const MAX_ADJUSTMENTS = 8;

const NOTHING_COPIED = 'Pick a style first — select a shape and click "Copy Style".';

/* ── Capture ──────────────────────────────────────────────────────── */

/** Queue the loads a full style capture needs off one shape. */
function loadForCapture(shape: PowerPoint.Shape): {
  frame: PowerPoint.TextFrame;
  adjustments: PowerPoint.Adjustments;
} {
  shape.load("name,left,top,width,height,rotation,type");
  shape.fill.load("type,foregroundColor,transparency");
  shape.lineFormat.load("visible,color,weight,dashStyle,style,transparency");
  const frame = shape.getTextFrameOrNullObject();
  frame.load("isNullObject,leftMargin,topMargin,rightMargin,bottomMargin,wordWrap,verticalAlignment,autoSizeSetting");
  const adjustments = shape.adjustments;
  adjustments.load("count");
  return { frame, adjustments };
}

/**
 * Read a value that Office.js may refuse for the shape at hand (an unfilled
 * shape has no `foregroundColor`, a line-less shape no `color`, …). Office
 * surfaces those as a throw on property access after the sync, so every
 * optional read goes through here.
 */
function tryRead<T>(read: () => T): T | null {
  try {
    // `??` and not `||`: `false` (wordWrap), `0` (transparency) and `""` are all
    // legitimate Office values, and `||` would collapse them to null — which the
    // callers' own `?? 0` / `?? true` defaults would then silently overwrite.
    return read() ?? null;
  } catch {
    return null;
  }
}

function captureFill(shape: PowerPoint.Shape): { color: string | null; transparency: number | null } {
  const type = tryRead(() => String(shape.fill.type));
  if (type !== "Solid") return { color: null, transparency: null };
  return {
    color: tryRead(() => shape.fill.foregroundColor),
    transparency: tryRead(() => shape.fill.transparency),
  };
}

function captureLine(shape: PowerPoint.Shape): CopiedShapeStyle["line"] {
  if (tryRead(() => shape.lineFormat.visible) !== true) return null;
  const color = tryRead(() => shape.lineFormat.color);
  if (!color) return null;
  return {
    color,
    weight: tryRead(() => shape.lineFormat.weight) ?? 1,
    dashStyle: tryRead(() => String(shape.lineFormat.dashStyle)) ?? "Solid",
    style: tryRead(() => String(shape.lineFormat.style)) ?? "Single",
    transparency: tryRead(() => shape.lineFormat.transparency) ?? 0,
  };
}

function captureFont(font: PowerPoint.ShapeFont): CopiedShapeStyle["font"] {
  const snap = {
    name: tryRead(() => font.name) ?? undefined,
    size: tryRead(() => font.size) ?? undefined,
    color: tryRead(() => font.color) ?? undefined,
    bold: tryRead(() => font.bold) ?? undefined,
    italic: tryRead(() => font.italic) ?? undefined,
    underline: tryRead(() => (font.underline === null ? undefined : String(font.underline))) ?? undefined,
  };
  // A mixed-format run reports every field as null; nothing worth keeping then.
  return Object.values(snap).some((v) => v !== undefined) ? snap : null;
}

function captureTextFrame(frame: PowerPoint.TextFrame): CopiedShapeStyle["textFrame"] {
  if (frame.isNullObject) return null;
  return {
    margins: {
      left: tryRead(() => frame.leftMargin) ?? 0,
      top: tryRead(() => frame.topMargin) ?? 0,
      right: tryRead(() => frame.rightMargin) ?? 0,
      bottom: tryRead(() => frame.bottomMargin) ?? 0,
    },
    wordWrap: tryRead(() => frame.wordWrap) ?? true,
    verticalAlignment: tryRead(() => String(frame.verticalAlignment)) ?? "Top",
    autoSizeSetting: tryRead(() => String(frame.autoSizeSetting)) ?? "AutoSizeNone",
  };
}

/**
 * Snapshot the selection into the style clipboard ("Copy Style").
 * Returns the captured style so the panel can describe what was picked.
 */
export async function copyStyle(): Promise<CopiedStyle> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");

  const picked = await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select the shape whose style you want to copy.");

    const parts = sel.items.map((shape) => ({ shape, ...loadForCapture(shape) }));
    await context.sync();

    // Adjustment values need a second round-trip: `count` has to be known
    // before the individual `get(i)` calls can be queued.
    const handles = parts.map(({ adjustments }) => {
      const count = Math.min(tryRead(() => adjustments.count) ?? 0, MAX_ADJUSTMENTS);
      return Array.from({ length: count }, (_, i) => tryRead(() => adjustments.get(i)));
    });
    const fonts = parts.map(({ frame }) => (frame.isNullObject ? null : frame.textRange.font));
    for (const font of fonts) font?.load("name,size,color,bold,italic,underline");
    await context.sync();

    const shapes: CopiedShapeStyle[] = parts.map(({ shape, frame }, i) => {
      const fill = captureFill(shape);
      const font = fonts[i];
      return {
        width: shape.width,
        height: shape.height,
        left: shape.left,
        top: shape.top,
        rotation: tryRead(() => shape.rotation) ?? 0,
        fillColor: fill.color,
        fillTransparency: fill.transparency,
        line: captureLine(shape),
        font: font ? captureFont(font) : null,
        textFrame: captureTextFrame(frame),
        adjustments: (handles[i] ?? [])
          .map((r) => (r === null ? null : r.value))
          .filter((v): v is number => typeof v === "number"),
      };
    });

    return { shapes, sourceName: sel.items[0]?.name ?? "" } satisfies CopiedStyle;
  });

  setClipboard(picked);
  return picked;
}

/* ── Apply ────────────────────────────────────────────────────────── */

/** Which facets each Apply … tool writes. */
interface FacetPlan {
  width: boolean;
  height: boolean;
  position: boolean;
  rotation: boolean;
  fill: boolean;
  line: boolean;
  font: boolean;
  textFrame: boolean;
  adjustments: boolean;
}

const NO_FACETS: FacetPlan = {
  width: false,
  height: false,
  position: false,
  rotation: false,
  fill: false,
  line: false,
  font: false,
  textFrame: false,
  adjustments: false,
};

/**
 * Facet → plan. Mirrors the VSTO buttons one-for-one:
 *   width/height/size/position → geometry only
 *   shapeStyle                 → the visual style (rotation, fill, line, font,
 *                                frame) — what VSTO's copy/paste-over achieved
 *   textFormat                 → font + text-frame layout
 *   color                      → fill only (VSTO also rewrote the font colour
 *                                and margins here; that is `all`'s job now)
 *   outline                    → line only
 *   adjustments                → preset geometry handles ("Apply Proportions")
 *   all                        → everything except position
 */
function planFor(facet: ApplyStyleFacet): FacetPlan {
  switch (facet) {
    case "width":
      return { ...NO_FACETS, width: true };
    case "height":
      return { ...NO_FACETS, height: true };
    case "size":
      return { ...NO_FACETS, width: true, height: true };
    case "position":
      return { ...NO_FACETS, position: true };
    case "shapeStyle":
      return { ...NO_FACETS, rotation: true, fill: true, line: true, font: true, textFrame: true };
    case "textFormat":
      return { ...NO_FACETS, font: true, textFrame: true };
    case "color":
      return { ...NO_FACETS, fill: true };
    case "outline":
      return { ...NO_FACETS, line: true };
    case "adjustments":
      return { ...NO_FACETS, adjustments: true };
    case "all":
      return {
        ...NO_FACETS,
        width: true,
        height: true,
        rotation: true,
        fill: true,
        line: true,
        font: true,
        textFrame: true,
        adjustments: true,
      };
    default:
      return assertNever(facet);
  }
}

function applyGeometry(shape: PowerPoint.Shape, src: CopiedShapeStyle, plan: FacetPlan): void {
  if (plan.width) shape.width = src.width;
  if (plan.height) shape.height = src.height;
  if (plan.position) {
    shape.left = src.left;
    shape.top = src.top;
  }
  // VSTO's Apply Shape Style pasted the source shape over the target, so the
  // source's rotation came with it — kept here for the same two tools.
  if (plan.rotation) shape.rotation = src.rotation;
}

function applyFill(shape: PowerPoint.Shape, src: CopiedShapeStyle): void {
  if (src.fillColor === null) {
    shape.fill.clear();
    return;
  }
  shape.fill.setSolidColor(src.fillColor);
  if (src.fillTransparency !== null) shape.fill.transparency = src.fillTransparency;
}

function applyLine(shape: PowerPoint.Shape, src: CopiedShapeStyle): void {
  const line = src.line;
  if (!line) {
    shape.lineFormat.visible = false;
    return;
  }
  shape.lineFormat.visible = true;
  shape.lineFormat.color = line.color;
  shape.lineFormat.weight = line.weight;
  shape.lineFormat.dashStyle = line.dashStyle as PowerPoint.ShapeLineDashStyle;
  shape.lineFormat.style = line.style as PowerPoint.ShapeLineStyle;
  shape.lineFormat.transparency = line.transparency;
}

function applyFrameLayout(frame: PowerPoint.TextFrame, layout: NonNullable<CopiedShapeStyle["textFrame"]>): void {
  const margins: TextMargins = layout.margins;
  frame.leftMargin = margins.left;
  frame.topMargin = margins.top;
  frame.rightMargin = margins.right;
  frame.bottomMargin = margins.bottom;
  frame.wordWrap = layout.wordWrap;
  frame.verticalAlignment = layout.verticalAlignment as PowerPoint.TextVerticalAlignment;
  frame.autoSizeSetting = layout.autoSizeSetting as PowerPoint.ShapeAutoSize;
}

function applyFontSnap(font: PowerPoint.ShapeFont, snap: NonNullable<CopiedShapeStyle["font"]>): void {
  if (snap.name !== undefined) font.name = snap.name;
  if (snap.size !== undefined) font.size = snap.size;
  if (snap.color !== undefined) font.color = snap.color;
  if (snap.bold !== undefined) font.bold = snap.bold;
  if (snap.italic !== undefined) font.italic = snap.italic;
  if (snap.underline !== undefined) font.underline = snap.underline as PowerPoint.ShapeFontUnderlineStyle;
}

function applyAdjustmentHandles(shape: PowerPoint.Shape, values: readonly number[]): void {
  // A target with fewer handles than the source rejects the extra indices;
  // each `set` is guarded so one mismatch doesn't abort the whole batch.
  values.forEach((value, i) => {
    try {
      shape.adjustments.set(i, value);
    } catch {
      /* target geometry has no handle i — expected for mixed selections */
    }
  });
}

/**
 * Write the picked style's `facet` onto the current selection.
 *
 * The picked shapes cycle over the selection (VSTO's `(j + 1) % count`), so a
 * 2-shape pick alternates across a 6-shape selection.
 */
export async function applyCopiedStyle(facet: ApplyStyleFacet): Promise<void> {
  if (!hasPpt()) throw new Error("This tool works only in PowerPoint.");
  const style = clipboard;
  if (!style || style.shapes.length === 0) throw new Error(NOTHING_COPIED);
  const plan = planFor(facet);

  await ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select the shapes to apply the copied style to.");

    const needsFrame = plan.font || plan.textFrame;
    const frames = needsFrame ? sel.items.map((shape) => shape.getTextFrameOrNullObject()) : [];
    for (const frame of frames) frame.load("isNullObject");
    await context.sync();

    sel.items.forEach((shape, i) => {
      const src = style.shapes[i % style.shapes.length]!;
      applyGeometry(shape, src, plan);
      if (plan.fill) applyFill(shape, src);
      if (plan.line) applyLine(shape, src);
      if (plan.adjustments && src.adjustments.length > 0) applyAdjustmentHandles(shape, src.adjustments);

      const frame = frames[i];
      if (!frame || frame.isNullObject) return;
      if (plan.textFrame && src.textFrame) applyFrameLayout(frame, src.textFrame);
      if (plan.font && src.font) applyFontSnap(frame.textRange.font, src.font);
    });
    await context.sync();
  });
}

/** Convenience wrappers — one per button in the Copy Style group. */
export const applyWidth = (): Promise<void> => applyCopiedStyle("width");
export const applyHeight = (): Promise<void> => applyCopiedStyle("height");
export const applySize = (): Promise<void> => applyCopiedStyle("size");
export const applyPosition = (): Promise<void> => applyCopiedStyle("position");
export const applyShapeStyle = (): Promise<void> => applyCopiedStyle("shapeStyle");
export const applyTextFormat = (): Promise<void> => applyCopiedStyle("textFormat");
export const applyFillColor = (): Promise<void> => applyCopiedStyle("color");
export const applyOutline = (): Promise<void> => applyCopiedStyle("outline");
export const applyProportions = (): Promise<void> => applyCopiedStyle("adjustments");
export const applyAllFormatting = (): Promise<void> => applyCopiedStyle("all");
